import { DsmGrid } from '@dsm-dcs/design-system-react';
import { useEffect, useRef } from 'react';
import { cloneDeep } from 'lodash';
import { useFormContext } from 'react-hook-form';
import { useIntl } from '../../../../../../_metronic/i18n/customUseIntl';
import { processAndStageStylesV2 } from '../../../../../../_metronic/layout';
import RowTextFieldWithMetrics from '../../../../../modules/Farms/Intervention/RowControlledTextFieldWithMetrics2';
import { UserProfilePrefs } from '../../../../../modules/Helpers/UserProfilePrefs';
import { PigFatteningStageData } from '../../../../models/Baseline/PigBaseline';
import {
  PigIntervention,
  PigOutputFattening,
} from '../../../../models/Intervention';
import { unitLong } from '../../../../utils/unit-utils';
import DsmButtonControlGroup from '../../../helpers/DsmButtonControlGroup';
import { DialogContainer } from '../../CommonDataParts/DialogContainer2';
import { FormType } from '../../common';
import { PigInterventionFormDialogProps } from '../CommonDataParts/InterventionTypes';

const PFInterventionOutputFormDialog = ({
  formType = FormType.Add,
  stageIndex = 0,
  formVisible,
  handleCancel,
  baseline,
}: PigInterventionFormDialogProps) => {
  const intl = useIntl();
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const classes = processAndStageStylesV2() as any;

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const userProfile = UserProfilePrefs.getInstance();
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
  const userUOM = userProfile.getUserUnitPrefs();
  const barnOutputMassPrecision =
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
  userProfile.getUnitBarnOutputMassPrecision() as number;

  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  const barnOutputMassUnit = userUOM?.unitBarnOutputMass

  ? // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    unitLong(userUOM.unitBarnOutputMass as string)
  : ('kg' as string);

  const currResetValue = useRef<PigOutputFattening>();

  const formTitle = intl.formatMessage({
    id: 'SUSTELL.PROCESS.DIALOG.STAGE.OUTPUT.TITLE',
  });
  const fieldItemPrefix = `stages.${stageIndex}.stageData.output`;
  const fc = useFormContext();

  const handleResetClick = () => {
    if (currResetValue.current) {
      const resetObject = { ...fc.getValues() } as PigIntervention;
      const stageData = resetObject?.stages?.[stageIndex]?.stageData;
      if (stageData?.output) {
        stageData.output = {
          ...currResetValue.current,
        };
        fc.reset(resetObject, {
          errors: true,
        });
      }
    }
    handleCancel();
  };

  const output = (
    baseline?.stages?.[stageIndex]?.stageData as PigFatteningStageData
  )?.output;

  useEffect(() => {
    if (formVisible) {
      if (!fc.getValues(fieldItemPrefix)) {
        currResetValue.current = {};
      } else {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        currResetValue.current = cloneDeep(fc.getValues(fieldItemPrefix));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formVisible]);

  return (
    <DialogContainer
      formVisible={formVisible}
      handleClose={handleResetClick}
      iconCode="arrows/arrow-circle-broken-right"
      formTitle={formTitle}
      datasetType="intervention"
    >
      <DsmGrid className={classes.dsmGridOneColumnIntervention}>
        <RowTextFieldWithMetrics
          name={`${fieldItemPrefix}.averageAgePigs`}
          label={intl.formatMessage({
            id: 'SUSTELL.STAGE.PIGS.FATTENING.OUTPUT.AGE.PIGS',
          })}
          type="number"
          tooltip={intl.formatMessage({
            id: 'SUSTELL.STAGE.PIGS.FATTENING.OUTPUT.AGE.PIGS.INTERVENTION.TOOLTIP',
          })}
          metricUnit={intl.formatMessage({
            id: 'SUSTELL.STAGE.PIGS.FATTENING.OUTPUT.AGE.PIGS.UNIT',
          })}
          baseline={output?.averageAgePigs}
          disabled={formType === FormType.View}
        />
        <RowTextFieldWithMetrics
          name={`${fieldItemPrefix}.averageWeightPigs`}
          label={intl.formatMessage({
            id: 'SUSTELL.STAGE.PIGS.FATTENING.OUTPUT.WEIGHT.PIGS',
          })}
          type="number"
          tooltip={intl.formatMessage({
            id: 'SUSTELL.STAGE.PIGS.FATTENING.OUTPUT.WEIGHT.PIGS.INTERVENTION.TOOLTIP',
          })}
          metricUnit={barnOutputMassUnit}
          precision={barnOutputMassPrecision}
          baseline={output?.averageWeightPigs}
          disabled={formType === FormType.View}
        />
        <RowTextFieldWithMetrics
          name={`${fieldItemPrefix}.mortalityPigs`}
          label={intl.formatMessage({
            id: 'SUSTELL.STAGE.PIGS.FATTENING.OUTPUT.MORTALITY.PIGS',
          })}
          type="number"
          tooltip={intl.formatMessage({
            id: 'SUSTELL.STAGE.PIGS.FATTENING.OUTPUT.MORTALITY.PIGS.INTERVENTION.TOOLTIP',
          })}
          metricUnit=""
          baseline={output?.mortalityPigs}
          disabled={formType === FormType.View}
        />
        <RowTextFieldWithMetrics
          name={`${fieldItemPrefix}.pigsToStage`}
          label={intl.formatMessage({
            id: 'SUSTELL.STAGE.PIGS.FATTENING.OUTPUT.PIGS.TO_ANOTHER_STAGE',
          })}
          type="number"
          tooltip={intl.formatMessage({
            id: 'SUSTELL.STAGE.PIGS.FATTENING.OUTPUT.PIGS.TO_ANOTHER_STAGE.INTERVENTION.TOOLTIP',
          })}
          metricUnit=""
          baseline={output?.pigsToStage}
          disabled={formType === FormType.View}
        />
        <RowTextFieldWithMetrics
          name={`${fieldItemPrefix}.soldPigs`}
          label={intl.formatMessage({
            id: 'SUSTELL.STAGE.PIGS.FATTENING.OUTPUT.PIGS.SOLD',
          })}
          type="number"
          tooltip={intl.formatMessage({
            id: 'SUSTELL.STAGE.PIGS.FATTENING.OUTPUT.PIGS.SOLD.INTERVENTION.TOOLTIP',
          })}
          metricUnit=""
          baseline={output?.soldPigs}
          disabled={formType === FormType.View}
        />
        <RowTextFieldWithMetrics
          name={`${fieldItemPrefix}.priceSoldPigs`}
          label={intl.formatMessage({
            id: 'SUSTELL.STAGE.PIGS.FATTENING.OUTPUT.PIGS.PRICE_SOLD',
          })}
          type="number"
          tooltip={intl.formatMessage({
            id: 'SUSTELL.STAGE.PIGS.FATTENING.OUTPUT.PIGS.PRICE_SOLD.INTERVENTION.TOOLTIP',
          })}
          metricUnit={intl.formatMessage({
            id: 'SUSTELL.STAGE.PIGS.FATTENING.OUTPUT.PRICE_SOLD.PLACEHOLDER',
          })}
          baseline={output?.priceSoldPigs}
          disabled={formType === FormType.View}
        />
      </DsmGrid>
      <DsmButtonControlGroup
        cancelHandler={handleResetClick}
        saveHandler={handleCancel}
        saveLabel={intl.formatMessage({ id: 'GENERAL.CONFIRM' })}
      />
    </DialogContainer>
  );
};

export default PFInterventionOutputFormDialog;
